/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { UserRolesEnum } from "@malmur-technologies/medicalsystem-shared";

/**
 * Enum defining all system entities that can have permissions assigned.
 * Maps to application features and pages for role-based access control.
 * Used with ActionPermissions to create granular permission strings.
 */
export enum EntityPermissions {
  ACCOUNTING = "accounting",
  ACCOUNTING_MOVEMENTS = "accountingMovements",
  ACTIVITY_LOGS = "activityLogs",
  APPOINTMENTS = "appointments",
  BILLING_ENCOUNTER = "billingEncounter",
  CLAIM = "claim",
  DASHBOARD = "dashboard",
  DOCTORS = "doctors",
  EDIT_DOCTORS = "editDoctor",
  EDIT_NURSE = "editNurse",
  EDIT_PATIENTS = "editPatient",
  ELECTRONIC_TRANSACTION = "electronicTransaction",
  FACILITY = "facility",
  GENERAL_SETTINGS = "generalSettings",
  HPI_PAGE = "hpisPage",
  INSURANCE = "insurance",
  INSURANCE_TRANSACTION = "insuranceTransaction",
  NEW_APPOINTMENT = "newAppointment",
  NEW_DOCTORS = "newDoctor",
  NEW_OFFICE_VISIT = "newOfficeVisit",
  NEW_PATIENTS = "newPatient",
  NEW_PROVIDER = "newProvider",
  NEW_USER = "newUser",
  NURSE = "nurse",
  OFFICE_VISIT = "officeVisit",
  PATIENT_HUB = "patientHub",
  PATIENT_INSURANCE = "patientInsurance",
  PATIENTS = "patients",
  PAYER = "payer",
  PAYMENTS = "payment",
  PHARMACY = "pharmacy",
  PRESCRIPTIONS = "prescriptions",
  PRICING = "pricing",
  PROGRESS_NOTE = "progressNote",
  RESOURCE = "resource",
  SWAGGER = "swagger",
  USER_PROFILE = "userProfile",
  USERS = "users",
  VIDEOCHAT = "videochat",
  VITALS_PAGE = "vitalsPage",
}

/**
 * Enum defining CRUD and special actions for permission control.
 * Combined with EntityPermissions to create specific permission strings like 'patients.create'.
 * Supports fine-grained authorization at the feature level.
 */
export enum ActionPermissions {
  VIEW = "view",
  CREATE = "create",
  EDIT = "edit",
  DELETE = "delete",
  SELECT_ROLES = "selectRoles",
  VIEW_DOCUMENTS = "viewDocuments",
  UPLOAD_DOCUMENTS = "uploadDocuments",
  PRINT = "print",
  TEMPLATES = "templates",
}

/**
 * Constant mapping entities to their available actions with fully-qualified permission strings.
 * Creates permission identifiers like 'patients.view', 'appointments.create' for authorization checks.
 * Central registry for all system permissions.
 */
export const PermissionsUser = {
  [EntityPermissions.ACCOUNTING]: {
    VIEW: `${EntityPermissions.ACCOUNTING}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.ACCOUNTING}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.ACCOUNTING}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.ACCOUNTING}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.ACCOUNTING_MOVEMENTS]: {
    VIEW: `${EntityPermissions.ACCOUNTING_MOVEMENTS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.ACCOUNTING_MOVEMENTS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.ACCOUNTING_MOVEMENTS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.ACCOUNTING_MOVEMENTS}.${ActionPermissions.DELETE}`,
    PRINT: `${EntityPermissions.ACCOUNTING_MOVEMENTS}.${ActionPermissions.PRINT}`,
  },
  [EntityPermissions.ACTIVITY_LOGS]: {
    VIEW: `${EntityPermissions.ACTIVITY_LOGS}.${ActionPermissions.VIEW}`,
  },
  [EntityPermissions.GENERAL_SETTINGS]: {
    VIEW: `${EntityPermissions.GENERAL_SETTINGS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.GENERAL_SETTINGS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.GENERAL_SETTINGS}.${ActionPermissions.EDIT}`,
  },
  [EntityPermissions.APPOINTMENTS]: {
    VIEW: `${EntityPermissions.APPOINTMENTS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.APPOINTMENTS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.APPOINTMENTS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.APPOINTMENTS}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.BILLING_ENCOUNTER]: {
    VIEW: `${EntityPermissions.BILLING_ENCOUNTER}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.BILLING_ENCOUNTER}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.BILLING_ENCOUNTER}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.BILLING_ENCOUNTER}.${ActionPermissions.DELETE}`,
    PRINT: `${EntityPermissions.BILLING_ENCOUNTER}.${ActionPermissions.PRINT}`,
  },
  [EntityPermissions.DASHBOARD]: {
    VIEW: `${EntityPermissions.DASHBOARD}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.DASHBOARD}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.DASHBOARD}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.DASHBOARD}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.DOCTORS]: {
    VIEW: `${EntityPermissions.DOCTORS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.DOCTORS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.DOCTORS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.DOCTORS}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.EDIT_DOCTORS]: {
    VIEW: `${EntityPermissions.EDIT_DOCTORS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.EDIT_DOCTORS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.EDIT_DOCTORS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.EDIT_DOCTORS}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.EDIT_NURSE]: {
    VIEW: `${EntityPermissions.EDIT_NURSE}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.EDIT_NURSE}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.EDIT_NURSE}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.EDIT_NURSE}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.EDIT_PATIENTS]: {
    VIEW: `${EntityPermissions.EDIT_PATIENTS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.EDIT_PATIENTS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.EDIT_PATIENTS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.EDIT_PATIENTS}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.ELECTRONIC_TRANSACTION]: {
    VIEW: `${EntityPermissions.ELECTRONIC_TRANSACTION}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.ELECTRONIC_TRANSACTION}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.ELECTRONIC_TRANSACTION}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.ELECTRONIC_TRANSACTION}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.FACILITY]: {
    VIEW: `${EntityPermissions.FACILITY}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.FACILITY}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.FACILITY}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.FACILITY}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.HPI_PAGE]: {
    VIEW: `${EntityPermissions.HPI_PAGE}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.HPI_PAGE}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.HPI_PAGE}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.HPI_PAGE}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.INSURANCE]: {
    VIEW: `${EntityPermissions.INSURANCE}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.INSURANCE}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.INSURANCE}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.INSURANCE}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.INSURANCE_TRANSACTION]: {
    VIEW: `${EntityPermissions.INSURANCE_TRANSACTION}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.INSURANCE_TRANSACTION}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.INSURANCE_TRANSACTION}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.INSURANCE_TRANSACTION}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.NEW_APPOINTMENT]: {
    VIEW: `${EntityPermissions.NEW_APPOINTMENT}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.NEW_APPOINTMENT}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.NEW_APPOINTMENT}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.NEW_APPOINTMENT}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.NEW_DOCTORS]: {
    VIEW: `${EntityPermissions.NEW_DOCTORS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.NEW_DOCTORS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.NEW_DOCTORS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.NEW_DOCTORS}.${ActionPermissions.DELETE}`,
    SELECT_ROLES: `${EntityPermissions.NEW_DOCTORS}.${ActionPermissions.SELECT_ROLES}`,
  },
  [EntityPermissions.NEW_OFFICE_VISIT]: {
    VIEW: `${EntityPermissions.NEW_OFFICE_VISIT}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.NEW_OFFICE_VISIT}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.NEW_OFFICE_VISIT}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.NEW_OFFICE_VISIT}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.NEW_PATIENTS]: {
    VIEW: `${EntityPermissions.NEW_PATIENTS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.NEW_PATIENTS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.NEW_PATIENTS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.NEW_PATIENTS}.${ActionPermissions.DELETE}`,
    SELECT_ROLES: `${EntityPermissions.NEW_PATIENTS}.${ActionPermissions.SELECT_ROLES}`,
  },
  [EntityPermissions.NEW_PROVIDER]: {
    VIEW: `${EntityPermissions.NEW_PROVIDER}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.NEW_PROVIDER}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.NEW_PROVIDER}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.NEW_PROVIDER}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.NEW_USER]: {
    VIEW: `${EntityPermissions.NEW_USER}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.NEW_USER}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.NEW_USER}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.NEW_USER}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.NURSE]: {
    VIEW: `${EntityPermissions.NURSE}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.NURSE}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.NURSE}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.NURSE}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.OFFICE_VISIT]: {
    VIEW: `${EntityPermissions.OFFICE_VISIT}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.OFFICE_VISIT}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.OFFICE_VISIT}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.OFFICE_VISIT}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.PATIENT_HUB]: {
    VIEW: `${EntityPermissions.PATIENT_HUB}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.PATIENT_HUB}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.PATIENT_HUB}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.PATIENT_HUB}.${ActionPermissions.DELETE}`,
    VIEW_DOCUMENTS: `${EntityPermissions.PATIENT_HUB}.${ActionPermissions.VIEW_DOCUMENTS}`,
    UPLOAD_DOCUMENTS: `${EntityPermissions.PATIENT_HUB}.${ActionPermissions.UPLOAD_DOCUMENTS}`,
  },
  [EntityPermissions.PATIENT_INSURANCE]: {
    VIEW: `${EntityPermissions.PATIENT_INSURANCE}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.PATIENT_INSURANCE}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.PATIENT_INSURANCE}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.PATIENT_INSURANCE}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.PATIENTS]: {
    VIEW: `${EntityPermissions.PATIENTS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.PATIENTS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.PATIENTS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.PATIENTS}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.PAYER]: {
    VIEW: `${EntityPermissions.PAYER}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.PAYER}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.PAYER}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.PAYER}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.PAYMENTS]: {
    VIEW: `${EntityPermissions.PAYMENTS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.PAYMENTS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.PAYMENTS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.PAYMENTS}.${ActionPermissions.DELETE}`,
    UPLOAD_DOCUMENTS: `${EntityPermissions.PAYMENTS}.${ActionPermissions.UPLOAD_DOCUMENTS}`,
  },
  [EntityPermissions.PHARMACY]: {
    VIEW: `${EntityPermissions.PHARMACY}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.PHARMACY}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.PHARMACY}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.PHARMACY}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.PRESCRIPTIONS]: {
    VIEW: `${EntityPermissions.PRESCRIPTIONS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.PRESCRIPTIONS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.PRESCRIPTIONS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.PRESCRIPTIONS}.${ActionPermissions.DELETE}`,
    PRINT: `${EntityPermissions.PRESCRIPTIONS}.${ActionPermissions.PRINT}`,
  },
  [EntityPermissions.PRICING]: {
    VIEW: `${EntityPermissions.PRICING}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.PRICING}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.PRICING}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.PRICING}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.PROGRESS_NOTE]: {
    VIEW: `${EntityPermissions.PROGRESS_NOTE}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.PROGRESS_NOTE}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.PROGRESS_NOTE}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.PROGRESS_NOTE}.${ActionPermissions.DELETE}`,
    PRINT: `${EntityPermissions.PROGRESS_NOTE}.${ActionPermissions.PRINT}`,
    TEMPLATES: `${EntityPermissions.PROGRESS_NOTE}.${ActionPermissions.TEMPLATES}`,
  },
  [EntityPermissions.RESOURCE]: {
    VIEW: `${EntityPermissions.RESOURCE}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.RESOURCE}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.RESOURCE}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.RESOURCE}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.SWAGGER]: {
    VIEW: `${EntityPermissions.SWAGGER}.${ActionPermissions.VIEW}`
  },
  [EntityPermissions.USER_PROFILE]: {
    VIEW: `${EntityPermissions.USER_PROFILE}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.USER_PROFILE}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.USER_PROFILE}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.USER_PROFILE}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.USERS]: {
    VIEW: `${EntityPermissions.USERS}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.USERS}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.USERS}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.USERS}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.VIDEOCHAT]: {
    VIEW: `${EntityPermissions.VIDEOCHAT}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.VIDEOCHAT}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.VIDEOCHAT}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.VIDEOCHAT}.${ActionPermissions.DELETE}`,
  },
  [EntityPermissions.VITALS_PAGE]: {
    VIEW: `${EntityPermissions.VITALS_PAGE}.${ActionPermissions.VIEW}`,
    CREATE: `${EntityPermissions.VITALS_PAGE}.${ActionPermissions.CREATE}`,
    EDIT: `${EntityPermissions.VITALS_PAGE}.${ActionPermissions.EDIT}`,
    DELETE: `${EntityPermissions.VITALS_PAGE}.${ActionPermissions.DELETE}`,
  },
};

/**
 * Constant defining permission assignments for each user role.
 * Maps UserRolesEnum (ROOT, ADMIN, DOCTOR, NURSE, etc.) to arrays of allowed permissions.
 * Implements role-based access control (RBAC) for the medical record system.
 * Ensures ISO-27001 compliant access controls with principle of least privilege.
 */
export const rolePermissions = {
  [UserRolesEnum.ROOT]: [
    PermissionsUser[EntityPermissions.PRICING].VIEW,
    PermissionsUser[EntityPermissions.PRICING].CREATE,
    PermissionsUser[EntityPermissions.PRICING].EDIT,
    PermissionsUser[EntityPermissions.PRICING].DELETE,

    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].CREATE,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].EDIT,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].DELETE,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].PRINT,

    PermissionsUser[EntityPermissions.PRESCRIPTIONS].VIEW,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].CREATE,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].EDIT,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].DELETE,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].PRINT,

    PermissionsUser[EntityPermissions.PAYER].VIEW,
    PermissionsUser[EntityPermissions.PAYER].CREATE,
    PermissionsUser[EntityPermissions.PAYER].EDIT,
    PermissionsUser[EntityPermissions.PAYER].DELETE,

    PermissionsUser[EntityPermissions.PAYMENTS].VIEW,
    PermissionsUser[EntityPermissions.PAYMENTS].CREATE,
    PermissionsUser[EntityPermissions.PAYMENTS].EDIT,
    PermissionsUser[EntityPermissions.PAYMENTS].DELETE,
    PermissionsUser[EntityPermissions.PAYMENTS].UPLOAD_DOCUMENTS,

    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].VIEW,
    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].CREATE,
    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].EDIT,
    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].DELETE,

    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].VIEW,
    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].CREATE,
    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].EDIT,
    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].DELETE,

    PermissionsUser[EntityPermissions.ACCOUNTING].VIEW,
    PermissionsUser[EntityPermissions.ACCOUNTING].CREATE,
    PermissionsUser[EntityPermissions.ACCOUNTING].EDIT,
    PermissionsUser[EntityPermissions.ACCOUNTING].DELETE,

    PermissionsUser[EntityPermissions.DASHBOARD].VIEW,

    PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,
    PermissionsUser[EntityPermissions.APPOINTMENTS].CREATE,
    PermissionsUser[EntityPermissions.APPOINTMENTS].EDIT,

    PermissionsUser[EntityPermissions.NEW_PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.NEW_PATIENTS].CREATE,
    PermissionsUser[EntityPermissions.NEW_PATIENTS].EDIT,
    PermissionsUser[EntityPermissions.NEW_PATIENTS].DELETE,
    PermissionsUser[EntityPermissions.NEW_PATIENTS].SELECT_ROLES,

    PermissionsUser[EntityPermissions.EDIT_PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.EDIT_PATIENTS].CREATE,
    PermissionsUser[EntityPermissions.EDIT_PATIENTS].EDIT,
    PermissionsUser[EntityPermissions.EDIT_PATIENTS].DELETE,

    PermissionsUser[EntityPermissions.PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.PATIENTS].CREATE,
    PermissionsUser[EntityPermissions.PATIENTS].EDIT,
    PermissionsUser[EntityPermissions.PATIENTS].DELETE,

    PermissionsUser[EntityPermissions.NEW_DOCTORS].VIEW,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].CREATE,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].EDIT,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].DELETE,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].SELECT_ROLES,

    PermissionsUser[EntityPermissions.EDIT_DOCTORS].VIEW,
    PermissionsUser[EntityPermissions.EDIT_DOCTORS].CREATE,
    PermissionsUser[EntityPermissions.EDIT_DOCTORS].EDIT,
    PermissionsUser[EntityPermissions.EDIT_DOCTORS].DELETE,

    PermissionsUser[EntityPermissions.DOCTORS].VIEW,
    PermissionsUser[EntityPermissions.DOCTORS].CREATE,
    PermissionsUser[EntityPermissions.DOCTORS].EDIT,
    PermissionsUser[EntityPermissions.DOCTORS].DELETE,

    PermissionsUser[EntityPermissions.NURSE].VIEW,
    PermissionsUser[EntityPermissions.NURSE].CREATE,
    PermissionsUser[EntityPermissions.NURSE].EDIT,
    PermissionsUser[EntityPermissions.NURSE].DELETE,

    PermissionsUser[EntityPermissions.EDIT_NURSE].VIEW,
    PermissionsUser[EntityPermissions.EDIT_NURSE].CREATE,
    PermissionsUser[EntityPermissions.EDIT_NURSE].EDIT,
    PermissionsUser[EntityPermissions.EDIT_NURSE].DELETE,

    PermissionsUser[EntityPermissions.NEW_PROVIDER].VIEW,
    PermissionsUser[EntityPermissions.NEW_PROVIDER].CREATE,
    PermissionsUser[EntityPermissions.NEW_PROVIDER].EDIT,
    PermissionsUser[EntityPermissions.NEW_PROVIDER].DELETE,

    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].VIEW,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].CREATE,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].EDIT,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].PRINT,

    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].VIEW,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].CREATE,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].EDIT,

    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].CREATE,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].EDIT,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].TEMPLATES,

    PermissionsUser[EntityPermissions.NEW_USER].VIEW,
    PermissionsUser[EntityPermissions.NEW_USER].CREATE,
    PermissionsUser[EntityPermissions.NEW_USER].EDIT,
    PermissionsUser[EntityPermissions.NEW_USER].DELETE,

    PermissionsUser[EntityPermissions.USER_PROFILE].VIEW,
    PermissionsUser[EntityPermissions.USER_PROFILE].CREATE,
    PermissionsUser[EntityPermissions.USER_PROFILE].EDIT,
    PermissionsUser[EntityPermissions.USER_PROFILE].DELETE,

    PermissionsUser[EntityPermissions.HPI_PAGE].VIEW,
    PermissionsUser[EntityPermissions.HPI_PAGE].CREATE,
    PermissionsUser[EntityPermissions.HPI_PAGE].EDIT,
    PermissionsUser[EntityPermissions.HPI_PAGE].DELETE,

    PermissionsUser[EntityPermissions.VITALS_PAGE].VIEW,
    PermissionsUser[EntityPermissions.VITALS_PAGE].CREATE,
    PermissionsUser[EntityPermissions.VITALS_PAGE].EDIT,
    PermissionsUser[EntityPermissions.VITALS_PAGE].DELETE,

    PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW,
    PermissionsUser[EntityPermissions.PATIENT_HUB].DELETE,
    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW_DOCUMENTS,
    PermissionsUser[EntityPermissions.PATIENT_HUB].UPLOAD_DOCUMENTS,

    PermissionsUser[EntityPermissions.PHARMACY].VIEW,
    PermissionsUser[EntityPermissions.PHARMACY].CREATE,
    PermissionsUser[EntityPermissions.PHARMACY].EDIT,
    PermissionsUser[EntityPermissions.PHARMACY].DELETE,

    PermissionsUser[EntityPermissions.FACILITY].VIEW,
    PermissionsUser[EntityPermissions.FACILITY].CREATE,
    PermissionsUser[EntityPermissions.FACILITY].EDIT,
    PermissionsUser[EntityPermissions.FACILITY].DELETE,

    PermissionsUser[EntityPermissions.RESOURCE].VIEW,
    PermissionsUser[EntityPermissions.RESOURCE].CREATE,
    PermissionsUser[EntityPermissions.RESOURCE].EDIT,
    PermissionsUser[EntityPermissions.RESOURCE].DELETE,

    PermissionsUser[EntityPermissions.INSURANCE].VIEW,
    PermissionsUser[EntityPermissions.INSURANCE].CREATE,
    PermissionsUser[EntityPermissions.INSURANCE].EDIT,
    PermissionsUser[EntityPermissions.INSURANCE].DELETE,

    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].VIEW,
    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].CREATE,
    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].EDIT,
    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].DELETE,

    PermissionsUser[EntityPermissions.USERS].VIEW,
    PermissionsUser[EntityPermissions.USERS].CREATE,
    PermissionsUser[EntityPermissions.USERS].EDIT,
    PermissionsUser[EntityPermissions.USERS].DELETE,

    PermissionsUser[EntityPermissions.VIDEOCHAT].VIEW,
    PermissionsUser[EntityPermissions.VIDEOCHAT].CREATE,
    PermissionsUser[EntityPermissions.VIDEOCHAT].EDIT,
    PermissionsUser[EntityPermissions.VIDEOCHAT].DELETE,

    PermissionsUser[EntityPermissions.ACTIVITY_LOGS].VIEW,
    PermissionsUser[EntityPermissions.GENERAL_SETTINGS].VIEW,
    PermissionsUser[EntityPermissions.GENERAL_SETTINGS].CREATE,
    PermissionsUser[EntityPermissions.GENERAL_SETTINGS].EDIT,
  ],
  [UserRolesEnum.ADMIN]: [
    PermissionsUser[EntityPermissions.PRICING].VIEW,
    PermissionsUser[EntityPermissions.PRICING].CREATE,
    PermissionsUser[EntityPermissions.PRICING].EDIT,
    PermissionsUser[EntityPermissions.PRICING].DELETE,

    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].CREATE,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].EDIT,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].DELETE,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].PRINT,

    PermissionsUser[EntityPermissions.PRESCRIPTIONS].VIEW,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].PRINT,

    PermissionsUser[EntityPermissions.PAYER].VIEW,
    PermissionsUser[EntityPermissions.PAYER].CREATE,
    PermissionsUser[EntityPermissions.PAYER].EDIT,
    PermissionsUser[EntityPermissions.PAYER].DELETE,

    PermissionsUser[EntityPermissions.PAYMENTS].VIEW,
    PermissionsUser[EntityPermissions.PAYMENTS].CREATE,
    PermissionsUser[EntityPermissions.PAYMENTS].EDIT,
    PermissionsUser[EntityPermissions.PAYMENTS].DELETE,
    PermissionsUser[EntityPermissions.PAYMENTS].UPLOAD_DOCUMENTS,

    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].VIEW,
    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].CREATE,
    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].EDIT,
    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].DELETE,

    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].VIEW,
    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].CREATE,
    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].EDIT,
    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].DELETE,

    PermissionsUser[EntityPermissions.ACCOUNTING].VIEW,
    PermissionsUser[EntityPermissions.ACCOUNTING].CREATE,
    PermissionsUser[EntityPermissions.ACCOUNTING].EDIT,
    PermissionsUser[EntityPermissions.ACCOUNTING].DELETE,

    PermissionsUser[EntityPermissions.DASHBOARD].VIEW,

    PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,
    PermissionsUser[EntityPermissions.APPOINTMENTS].CREATE,
    PermissionsUser[EntityPermissions.APPOINTMENTS].EDIT,

    PermissionsUser[EntityPermissions.NEW_PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.NEW_PATIENTS].CREATE,
    PermissionsUser[EntityPermissions.NEW_PATIENTS].EDIT,
    PermissionsUser[EntityPermissions.NEW_PATIENTS].SELECT_ROLES,

    PermissionsUser[EntityPermissions.EDIT_PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.EDIT_PATIENTS].CREATE,
    PermissionsUser[EntityPermissions.EDIT_PATIENTS].EDIT,
    PermissionsUser[EntityPermissions.EDIT_PATIENTS].DELETE,

    PermissionsUser[EntityPermissions.PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.PATIENTS].CREATE,
    PermissionsUser[EntityPermissions.PATIENTS].EDIT,
    PermissionsUser[EntityPermissions.PATIENTS].DELETE,

    PermissionsUser[EntityPermissions.NEW_DOCTORS].VIEW,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].CREATE,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].EDIT,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].DELETE,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].SELECT_ROLES,

    PermissionsUser[EntityPermissions.EDIT_DOCTORS].VIEW,
    PermissionsUser[EntityPermissions.EDIT_DOCTORS].CREATE,
    PermissionsUser[EntityPermissions.EDIT_DOCTORS].EDIT,
    PermissionsUser[EntityPermissions.EDIT_DOCTORS].DELETE,

    PermissionsUser[EntityPermissions.DOCTORS].VIEW,
    PermissionsUser[EntityPermissions.DOCTORS].CREATE,
    PermissionsUser[EntityPermissions.DOCTORS].EDIT,
    PermissionsUser[EntityPermissions.DOCTORS].DELETE,

    PermissionsUser[EntityPermissions.NURSE].VIEW,
    PermissionsUser[EntityPermissions.NURSE].CREATE,
    PermissionsUser[EntityPermissions.NURSE].EDIT,
    PermissionsUser[EntityPermissions.NURSE].DELETE,

    PermissionsUser[EntityPermissions.EDIT_NURSE].VIEW,
    PermissionsUser[EntityPermissions.EDIT_NURSE].CREATE,
    PermissionsUser[EntityPermissions.EDIT_NURSE].EDIT,
    PermissionsUser[EntityPermissions.EDIT_NURSE].DELETE,

    PermissionsUser[EntityPermissions.NEW_PROVIDER].VIEW,
    PermissionsUser[EntityPermissions.NEW_PROVIDER].CREATE,
    PermissionsUser[EntityPermissions.NEW_PROVIDER].EDIT,
    PermissionsUser[EntityPermissions.NEW_PROVIDER].DELETE,

    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].VIEW,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].CREATE,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].EDIT,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].PRINT,

    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].VIEW,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].CREATE,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].EDIT,

    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT,

    PermissionsUser[EntityPermissions.NEW_USER].VIEW,
    PermissionsUser[EntityPermissions.NEW_USER].CREATE,
    PermissionsUser[EntityPermissions.NEW_USER].EDIT,
    PermissionsUser[EntityPermissions.NEW_USER].DELETE,

    PermissionsUser[EntityPermissions.USER_PROFILE].VIEW,
    PermissionsUser[EntityPermissions.USER_PROFILE].CREATE,
    PermissionsUser[EntityPermissions.USER_PROFILE].EDIT,
    PermissionsUser[EntityPermissions.USER_PROFILE].DELETE,

    PermissionsUser[EntityPermissions.HPI_PAGE].VIEW,

    PermissionsUser[EntityPermissions.VITALS_PAGE].VIEW,

    PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW,
    PermissionsUser[EntityPermissions.PATIENT_HUB].DELETE,
    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW_DOCUMENTS,
    PermissionsUser[EntityPermissions.PATIENT_HUB].UPLOAD_DOCUMENTS,

    PermissionsUser[EntityPermissions.PHARMACY].VIEW,
    PermissionsUser[EntityPermissions.PHARMACY].CREATE,
    PermissionsUser[EntityPermissions.PHARMACY].EDIT,
    PermissionsUser[EntityPermissions.PHARMACY].DELETE,

    PermissionsUser[EntityPermissions.FACILITY].VIEW,
    PermissionsUser[EntityPermissions.FACILITY].CREATE,
    PermissionsUser[EntityPermissions.FACILITY].EDIT,
    PermissionsUser[EntityPermissions.FACILITY].DELETE,

    PermissionsUser[EntityPermissions.RESOURCE].VIEW,
    PermissionsUser[EntityPermissions.RESOURCE].CREATE,
    PermissionsUser[EntityPermissions.RESOURCE].EDIT,
    PermissionsUser[EntityPermissions.RESOURCE].DELETE,

    PermissionsUser[EntityPermissions.INSURANCE].VIEW,
    PermissionsUser[EntityPermissions.INSURANCE].CREATE,
    PermissionsUser[EntityPermissions.INSURANCE].EDIT,
    PermissionsUser[EntityPermissions.INSURANCE].DELETE,

    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].VIEW,
    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].CREATE,
    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].EDIT,
    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].DELETE,

    PermissionsUser[EntityPermissions.USERS].VIEW,
    PermissionsUser[EntityPermissions.USERS].CREATE,
    PermissionsUser[EntityPermissions.USERS].EDIT,
    PermissionsUser[EntityPermissions.USERS].DELETE,

    PermissionsUser[EntityPermissions.ACTIVITY_LOGS].VIEW,

    PermissionsUser[EntityPermissions.GENERAL_SETTINGS].VIEW,
    PermissionsUser[EntityPermissions.GENERAL_SETTINGS].CREATE,
    PermissionsUser[EntityPermissions.GENERAL_SETTINGS].EDIT,
  ],
  [UserRolesEnum.MAINTAINER]: [

    PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,

    PermissionsUser[EntityPermissions.PRICING].VIEW,

    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,

    PermissionsUser[EntityPermissions.PRESCRIPTIONS].VIEW,

    PermissionsUser[EntityPermissions.PAYER].VIEW,

    PermissionsUser[EntityPermissions.PAYMENTS].VIEW,

    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].VIEW,

    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].VIEW,

    PermissionsUser[EntityPermissions.ACCOUNTING].VIEW,

    PermissionsUser[EntityPermissions.DASHBOARD].VIEW,

    PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,

    PermissionsUser[EntityPermissions.PATIENTS].VIEW,

    PermissionsUser[EntityPermissions.DOCTORS].VIEW,

    PermissionsUser[EntityPermissions.NURSE].VIEW,

    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].VIEW,

    PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,

    PermissionsUser[EntityPermissions.USER_PROFILE].VIEW,

    PermissionsUser[EntityPermissions.HPI_PAGE].VIEW,

    PermissionsUser[EntityPermissions.VITALS_PAGE].VIEW,

    PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW,

    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW,

    PermissionsUser[EntityPermissions.PHARMACY].VIEW,

    PermissionsUser[EntityPermissions.FACILITY].VIEW,

    PermissionsUser[EntityPermissions.RESOURCE].VIEW,

    PermissionsUser[EntityPermissions.INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.USERS].VIEW,

    PermissionsUser[EntityPermissions.ACTIVITY_LOGS].VIEW,

    PermissionsUser[EntityPermissions.GENERAL_SETTINGS].VIEW,
  ],
  [UserRolesEnum.DOCTOR]: [

    PermissionsUser[EntityPermissions.PRICING].VIEW,

    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,

    PermissionsUser[EntityPermissions.PRESCRIPTIONS].VIEW,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].CREATE,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].EDIT,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].DELETE,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].PRINT,

    PermissionsUser[EntityPermissions.DASHBOARD].VIEW,

    PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,
    PermissionsUser[EntityPermissions.APPOINTMENTS].CREATE,
    PermissionsUser[EntityPermissions.APPOINTMENTS].EDIT,

    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].VIEW,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].CREATE,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].EDIT,

    PermissionsUser[EntityPermissions.PATIENTS].VIEW,

    PermissionsUser[EntityPermissions.EDIT_DOCTORS].EDIT,

    PermissionsUser[EntityPermissions.DOCTORS].VIEW,

    PermissionsUser[EntityPermissions.NURSE].VIEW,

    PermissionsUser[EntityPermissions.NEW_PROVIDER].VIEW,

    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].VIEW,

    PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].CREATE,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].EDIT,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].TEMPLATES,

    PermissionsUser[EntityPermissions.USER_PROFILE].VIEW,

    PermissionsUser[EntityPermissions.HPI_PAGE].VIEW,
    PermissionsUser[EntityPermissions.HPI_PAGE].CREATE,
    PermissionsUser[EntityPermissions.HPI_PAGE].EDIT,

    PermissionsUser[EntityPermissions.VITALS_PAGE].VIEW,
    PermissionsUser[EntityPermissions.VITALS_PAGE].CREATE,
    PermissionsUser[EntityPermissions.VITALS_PAGE].EDIT,

    PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW,

    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW,
    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW_DOCUMENTS,
    PermissionsUser[EntityPermissions.PATIENT_HUB].UPLOAD_DOCUMENTS,

    PermissionsUser[EntityPermissions.PHARMACY].VIEW,

    PermissionsUser[EntityPermissions.FACILITY].VIEW,

    PermissionsUser[EntityPermissions.RESOURCE].VIEW,

    PermissionsUser[EntityPermissions.INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.VIDEOCHAT].VIEW,
  ],
  [UserRolesEnum.HEAD_NURSE]: [
    PermissionsUser[EntityPermissions.PRICING].VIEW,

    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].CREATE,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].EDIT,

    PermissionsUser[EntityPermissions.PRESCRIPTIONS].VIEW,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].CREATE,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].EDIT,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].DELETE,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].PRINT,

    PermissionsUser[EntityPermissions.DASHBOARD].VIEW,

    PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,
    PermissionsUser[EntityPermissions.APPOINTMENTS].CREATE,
    PermissionsUser[EntityPermissions.APPOINTMENTS].EDIT,

    PermissionsUser[EntityPermissions.EDIT_PATIENTS].EDIT,

    PermissionsUser[EntityPermissions.PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.PATIENTS].EDIT,

    PermissionsUser[EntityPermissions.DOCTORS].VIEW,

    PermissionsUser[EntityPermissions.NURSE].VIEW,

    PermissionsUser[EntityPermissions.NEW_PROVIDER].VIEW,

    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].VIEW,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].CREATE,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].EDIT,

    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].VIEW,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].CREATE,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].EDIT,

    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].CREATE,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].EDIT,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].TEMPLATES,

    PermissionsUser[EntityPermissions.USER_PROFILE].VIEW,

    PermissionsUser[EntityPermissions.HPI_PAGE].VIEW,
    PermissionsUser[EntityPermissions.HPI_PAGE].CREATE,
    PermissionsUser[EntityPermissions.HPI_PAGE].EDIT,

    PermissionsUser[EntityPermissions.VITALS_PAGE].VIEW,
    PermissionsUser[EntityPermissions.VITALS_PAGE].CREATE,
    PermissionsUser[EntityPermissions.VITALS_PAGE].EDIT,

    PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW,
    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW_DOCUMENTS,
    PermissionsUser[EntityPermissions.PATIENT_HUB].UPLOAD_DOCUMENTS,

    PermissionsUser[EntityPermissions.PHARMACY].VIEW,

    PermissionsUser[EntityPermissions.FACILITY].VIEW,

    PermissionsUser[EntityPermissions.RESOURCE].VIEW,

    PermissionsUser[EntityPermissions.INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.VIDEOCHAT].VIEW,
  ],
  [UserRolesEnum.NURSE]: [
    PermissionsUser[EntityPermissions.PRICING].VIEW,

    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,

    PermissionsUser[EntityPermissions.PRESCRIPTIONS].VIEW,
    PermissionsUser[EntityPermissions.PRESCRIPTIONS].PRINT,

    PermissionsUser[EntityPermissions.DASHBOARD].VIEW,

    PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,

    PermissionsUser[EntityPermissions.PATIENTS].VIEW,

    PermissionsUser[EntityPermissions.DOCTORS].VIEW,

    PermissionsUser[EntityPermissions.NURSE].VIEW,

    PermissionsUser[EntityPermissions.NEW_PROVIDER].VIEW,

    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].CREATE,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].EDIT,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].TEMPLATES,

    PermissionsUser[EntityPermissions.USER_PROFILE].VIEW,

    PermissionsUser[EntityPermissions.HPI_PAGE].VIEW,
    PermissionsUser[EntityPermissions.HPI_PAGE].CREATE,
    PermissionsUser[EntityPermissions.HPI_PAGE].EDIT,

    PermissionsUser[EntityPermissions.VITALS_PAGE].VIEW,
    PermissionsUser[EntityPermissions.VITALS_PAGE].CREATE,
    PermissionsUser[EntityPermissions.VITALS_PAGE].EDIT,

    PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW,
    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW_DOCUMENTS,
    PermissionsUser[EntityPermissions.PATIENT_HUB].UPLOAD_DOCUMENTS,

    PermissionsUser[EntityPermissions.PHARMACY].VIEW,

    PermissionsUser[EntityPermissions.FACILITY].VIEW,

    PermissionsUser[EntityPermissions.RESOURCE].VIEW,

    PermissionsUser[EntityPermissions.INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.VIDEOCHAT].VIEW,
  ],
  [UserRolesEnum.PATIENT]: [
    PermissionsUser[EntityPermissions.PRICING].VIEW,

    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].PRINT,

    PermissionsUser[EntityPermissions.PRESCRIPTIONS].VIEW,

    PermissionsUser[EntityPermissions.PAYMENTS].VIEW,

    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].VIEW,

    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].VIEW,

    PermissionsUser[EntityPermissions.ACCOUNTING].VIEW,

    PermissionsUser[EntityPermissions.DASHBOARD].VIEW,

    PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,

    PermissionsUser[EntityPermissions.PATIENTS].VIEW,

    PermissionsUser[EntityPermissions.DOCTORS].VIEW,

    PermissionsUser[EntityPermissions.NURSE].VIEW,

    PermissionsUser[EntityPermissions.NEW_PROVIDER].VIEW,

    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].VIEW,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].PRINT,

    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].VIEW,

    PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT,

    PermissionsUser[EntityPermissions.USER_PROFILE].VIEW,

    PermissionsUser[EntityPermissions.VITALS_PAGE].VIEW,

    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW,

    PermissionsUser[EntityPermissions.PHARMACY].VIEW,

    PermissionsUser[EntityPermissions.FACILITY].VIEW,

    PermissionsUser[EntityPermissions.RESOURCE].VIEW,

    PermissionsUser[EntityPermissions.INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].VIEW,

    PermissionsUser[EntityPermissions.VIDEOCHAT].VIEW,
  ],
  [UserRolesEnum.SECRETARY_RECEPTIONIST]: [
    PermissionsUser[EntityPermissions.PRICING].VIEW,
    PermissionsUser[EntityPermissions.PRICING].CREATE,
    PermissionsUser[EntityPermissions.PRICING].EDIT,

    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].CREATE,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].EDIT,
    PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].PRINT,

    PermissionsUser[EntityPermissions.PRESCRIPTIONS].VIEW,

    PermissionsUser[EntityPermissions.PAYER].VIEW,
    PermissionsUser[EntityPermissions.PAYER].CREATE,
    PermissionsUser[EntityPermissions.PAYER].EDIT,

    PermissionsUser[EntityPermissions.PAYMENTS].VIEW,
    PermissionsUser[EntityPermissions.PAYMENTS].CREATE,
    PermissionsUser[EntityPermissions.PAYMENTS].EDIT,
    PermissionsUser[EntityPermissions.PAYMENTS].UPLOAD_DOCUMENTS,

    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].VIEW,
    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].CREATE,
    PermissionsUser[EntityPermissions.ELECTRONIC_TRANSACTION].EDIT,

    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].VIEW,
    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].CREATE,
    PermissionsUser[EntityPermissions.INSURANCE_TRANSACTION].EDIT,

    PermissionsUser[EntityPermissions.ACCOUNTING].VIEW,
    PermissionsUser[EntityPermissions.ACCOUNTING].CREATE,

    PermissionsUser[EntityPermissions.DASHBOARD].VIEW,

    PermissionsUser[EntityPermissions.APPOINTMENTS].VIEW,
    PermissionsUser[EntityPermissions.APPOINTMENTS].CREATE,
    PermissionsUser[EntityPermissions.APPOINTMENTS].EDIT,

    PermissionsUser[EntityPermissions.NEW_PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.NEW_PATIENTS].CREATE,
    PermissionsUser[EntityPermissions.NEW_PATIENTS].EDIT,

    PermissionsUser[EntityPermissions.EDIT_PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.EDIT_PATIENTS].CREATE,
    PermissionsUser[EntityPermissions.EDIT_PATIENTS].EDIT,

    PermissionsUser[EntityPermissions.PATIENTS].VIEW,
    PermissionsUser[EntityPermissions.PATIENTS].CREATE,
    PermissionsUser[EntityPermissions.PATIENTS].EDIT,

    PermissionsUser[EntityPermissions.NEW_DOCTORS].VIEW,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].CREATE,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].EDIT,
    PermissionsUser[EntityPermissions.NEW_DOCTORS].SELECT_ROLES,

    PermissionsUser[EntityPermissions.EDIT_DOCTORS].VIEW,
    PermissionsUser[EntityPermissions.EDIT_DOCTORS].CREATE,
    PermissionsUser[EntityPermissions.EDIT_DOCTORS].EDIT,

    PermissionsUser[EntityPermissions.DOCTORS].VIEW,
    PermissionsUser[EntityPermissions.DOCTORS].CREATE,
    PermissionsUser[EntityPermissions.DOCTORS].EDIT,

    PermissionsUser[EntityPermissions.NURSE].VIEW,
    PermissionsUser[EntityPermissions.NURSE].CREATE,
    PermissionsUser[EntityPermissions.NURSE].EDIT,

    PermissionsUser[EntityPermissions.EDIT_NURSE].VIEW,
    PermissionsUser[EntityPermissions.EDIT_NURSE].EDIT,

    PermissionsUser[EntityPermissions.NEW_PROVIDER].VIEW,
    PermissionsUser[EntityPermissions.NEW_PROVIDER].CREATE,
    PermissionsUser[EntityPermissions.NEW_PROVIDER].EDIT,

    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].VIEW,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].CREATE,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].EDIT,
    PermissionsUser[EntityPermissions.ACCOUNTING_MOVEMENTS].PRINT,

    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].VIEW,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].CREATE,
    PermissionsUser[EntityPermissions.NEW_APPOINTMENT].EDIT,

    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.NEW_OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW,
    PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT,

    PermissionsUser[EntityPermissions.USER_PROFILE].VIEW,

    PermissionsUser[EntityPermissions.OFFICE_VISIT].VIEW,
    PermissionsUser[EntityPermissions.OFFICE_VISIT].EDIT,

    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW,
    PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW_DOCUMENTS,
    PermissionsUser[EntityPermissions.PATIENT_HUB].UPLOAD_DOCUMENTS,

    PermissionsUser[EntityPermissions.PHARMACY].VIEW,
    PermissionsUser[EntityPermissions.PHARMACY].CREATE,
    PermissionsUser[EntityPermissions.PHARMACY].EDIT,
    PermissionsUser[EntityPermissions.PHARMACY].DELETE,

    PermissionsUser[EntityPermissions.FACILITY].VIEW,
    PermissionsUser[EntityPermissions.FACILITY].CREATE,
    PermissionsUser[EntityPermissions.FACILITY].EDIT,
    PermissionsUser[EntityPermissions.FACILITY].DELETE,

    PermissionsUser[EntityPermissions.RESOURCE].VIEW,
    PermissionsUser[EntityPermissions.RESOURCE].CREATE,
    PermissionsUser[EntityPermissions.RESOURCE].EDIT,
    PermissionsUser[EntityPermissions.RESOURCE].DELETE,

    PermissionsUser[EntityPermissions.INSURANCE].VIEW,
    PermissionsUser[EntityPermissions.INSURANCE].CREATE,
    PermissionsUser[EntityPermissions.INSURANCE].EDIT,
    PermissionsUser[EntityPermissions.INSURANCE].DELETE,

    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].VIEW,
    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].CREATE,
    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].EDIT,
    PermissionsUser[EntityPermissions.PATIENT_INSURANCE].DELETE,

    PermissionsUser[EntityPermissions.USERS].VIEW,

    PermissionsUser[EntityPermissions.GENERAL_SETTINGS].VIEW,
  ],
};

